/**
 * Pratica Status & Correzione — F-005 shared contracts (Phase 1, Task 1.2).
 *
 * Schemi Zod e tipi TypeScript condivisi tra API (Express) e frontend (React SPA)
 * per le funzionalità di:
 *  - lista pratiche attive (GET /api/v1/pratiche)
 *  - detail con timeline (GET /api/v1/pratiche/:id/status)
 *  - correzione self-service (PATCH /api/v1/pratiche/:id/correggi)
 *
 * Pattern di riferimento: `packages/shared/src/schemas/billingProfile.ts`
 *  - named export dello schema + `z.infer<typeof schema>` per il tipo inferito.
 *  - nessuna dipendenza da side-effects (db, fetch, env): puro contratto dati.
 */
import { z } from 'zod';
/* -------------------------------------------------------------------------- */
/* Stato pratica                                                              */
/* -------------------------------------------------------------------------- */
/**
 * Stato pratica — allineato VERBATIM all'enum DB Prisma `PraticaStato`
 * (apps/api/prisma/schema.prisma). Il contratto condiviso DEVE riflettere
 * esattamente i valori che il DB può produrre, altrimenti
 * `PraticaStatoSchema.parse()` lancerebbe a runtime quando una route
 * serializza una pratica reale (es. stato `IN_ELABORAZIONE` / `COMPLETATA`).
 *
 * Nota label utente (Phase 4 StateBadge): le etichette in italiano sono
 * derivate lato UI dai valori enum DB — NON inventare valori enum nuovi:
 *  - `IN_ELABORAZIONE` → "In lavorazione"
 *  - `COMPLETATA`      → "Registrata"
 *  Non esistono valori `ARCHIVIATA` / `REGISTRATA` nel DB: sono solo label.
 */
export const PraticaStatoSchema = z.enum([
    'BOZZA',
    'INVIATA',
    'IN_CARICO',
    'IN_ELABORAZIONE',
    'COMPLETATA',
    'BLOCCATA',
    'ANNULLATA',
]);
/**
 * Stati terminali: una pratica in questi stati non transita più, quindi il
 * polling lato client si ferma (refetchInterval → false). Allineati all'enum
 * DB: `COMPLETATA` (pratica registrata/conclusa) e `ANNULLATA` sono gli stati
 * effettivamente terminali. `BLOCCATA` NON è terminale (la pratica può tornare
 * in lavorazione dopo correzione self-service).
 */
export const STATI_TERMINALI = [
    'COMPLETATA',
    'ANNULLATA',
];
/* -------------------------------------------------------------------------- */
/* Lista pratiche attive                                                      */
/* -------------------------------------------------------------------------- */
export const PraticaListItemSchema = z.object({
    id: z.string(),
    stato: PraticaStatoSchema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    tipoContratto: z.string().optional(),
    nomeLocatore: z.string().optional(),
    nomeConduttore: z.string().optional(),
});
/* -------------------------------------------------------------------------- */
/* Timeline (da AuditLog)                                                     */
/* -------------------------------------------------------------------------- */
export const TimelineEventSchema = z.object({
    id: z.string(),
    action: z.string(),
    stato: PraticaStatoSchema.optional(),
    note: z.string().optional(),
    createdAt: z.string().datetime(),
});
/* -------------------------------------------------------------------------- */
/* Detail con timeline                                                        */
/* -------------------------------------------------------------------------- */
export const PraticaStatusDetailSchema = z.object({
    id: z.string(),
    stato: PraticaStatoSchema,
    operatorNoteVisible: z.string().nullable(),
    contestedFields: z.array(z.string()),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    timeline: z.array(TimelineEventSchema),
});
/* -------------------------------------------------------------------------- */
/* Response paginata lista                                                    */
/* -------------------------------------------------------------------------- */
export const PraticaListResponseSchema = z.object({
    items: z.array(PraticaListItemSchema),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
/* -------------------------------------------------------------------------- */
/* Correzione: request                                                        */
/* -------------------------------------------------------------------------- */
export const CorreggiRequestSchema = z.object({
    consenso: z.literal(true, {
        errorMap: () => ({ message: 'Il consenso è obbligatorio' }),
    }),
    datiStrutturati: z.record(z.unknown()),
});
/* -------------------------------------------------------------------------- */
/* Correzione: response                                                       */
/* -------------------------------------------------------------------------- */
export const CorreggiResponseSchema = z.object({
    id: z.string(),
    stato: PraticaStatoSchema,
    updatedAt: z.string().datetime(),
});
//# sourceMappingURL=pratica-status.js.map